import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { ButtonV3Component, MultiSelectV3Component, SelectOption, SelectV2Module, SnackbarV3Service } from '@gea/digital-ui-lib';
import { Divider } from 'primeng/divider';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UtilityService } from '../../../../shared/services/utility.service';
import { UserManagementService } from '../../../../services/user-management.service';
import { Target, TargetDTO } from '../../user-management.model';
import { finalize, Subject, switchMap } from 'rxjs';
import { StyleClass } from 'primeng/styleclass';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-add-grants',
  imports: [ButtonV3Component, Divider, FormsModule, ReactiveFormsModule, SelectV2Module, MultiSelectV3Component, StyleClass],
  templateUrl: './add-grants.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddGrantsComponent implements OnInit {
  grantOptions: SelectOption[] = [];
  targets: SelectOption[] = [];
  addGrantsForm = this.formBuilder.group({
    grant: [this.grantOptions[0], [Validators.required]],
    target: [this.targets, [Validators.required]],
  });
  userId = '';
  isTargetLoading = signal(false);
  addingGrants = signal(false);
  private grantSelect$ = new Subject<string>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: DynamicDialogRef,
    private readonly dialogConfig: DynamicDialogConfig,
    private readonly utilityService: UtilityService,
    private readonly userManagementService: UserManagementService,
    private readonly snackbarService: SnackbarV3Service,
    private readonly destroyed: DestroyRef
  ) {}

  ngOnInit(): void {
    this.grantOptions = this.utilityService.constructSelectOptions(this.dialogConfig.data?.grants) || [];
    this.userId = this.dialogConfig.data?.userId || '';
    this.listenToGrantChanges();
  }

  private listenToGrantChanges(): void {
    this.grantSelect$
      .pipe(
        switchMap((grant) => {
          this.isTargetLoading.set(true);
          return this.userManagementService.getTargetsForGrant(grant).pipe(
            finalize(() => {
              this.isTargetLoading.set(false);
              this.addGrantsForm.get('target')?.enable();
            })
          );
        }),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: (response: TargetDTO) => {
          this.targets = this.constructSelectOptions(response.targetEntities);
        },
      });
  }

  protected selectChanged(selectedGrant: SelectOption) {
    if (selectedGrant && selectedGrant.name) {
      this.isTargetLoading.set(true);
      /*TODO: Test this line and move it to geaui-select-v3 after angular 20 migration*/
      this.addGrantsForm.patchValue({ grant: selectedGrant });
      this.grantSelect$.next(selectedGrant.name);
    }
  }

  protected addGrantsToUser() {
    if (this.addingGrants()) return;

    if (this.addGrantsForm.get('grant') && this.addGrantsForm.get('target')) {
      this.addingGrants.set(true);
      const grant: SelectOption = this.addGrantsForm.get('grant')?.value;
      const targets: SelectOption[] = this.addGrantsForm.get('target')?.value!;
      this.userManagementService
        .setGrantToUser(grant, targets, this.userId)
        .pipe(
          finalize(() => this.addingGrants.set(false)),
          takeUntilDestroyed(this.destroyed)
        )
        .subscribe({
          next: () => {
            this.snackbarService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Grant has been successfully added to the user.',
            });
            this.dialogRef.close(true);
          },
        });
    }
  }

  constructSelectOptions(options: Target[]): SelectOption[] {
    const selectOptions: SelectOption[] = [];
    options.forEach((target) => {
      selectOptions.push({
        value: target.id,
        name: target.name,
      });
    });
    return selectOptions;
  }

  close(): void {
    this.dialogRef.close();
  }
}
